import React, { useState, useEffect } from "react";
import axios from "axios";
import SideBar from "../../components/SideBar";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import TableLists from "./TableLists";
import TableForm from "./TableForm";
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
} from "@mui/material";
import { toast } from "react-toastify";

function CreateTable() {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const [tableData, setTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false); // Manage Dialog open state
  const [selectedTable, setSelectedTable] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState(null);
  const [filter, setFilter] = useState(""); // State for filter input

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5); // Adjust items per page as needed

  // Fetch Data from the API
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/Tables/all`);
      if (response.status === 200) {
        setTableData(response.data.items || []);
        setFilteredTableData(response.data.items || []); // Set filtered data initially
        setError("");
      } else {
        setError(response.data.message || "Failed to fetch tables.");
      }
    } catch (err) {
      setError("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  };

  // Handle filter input change
  const handleFilterChange = (e) => {
    const searchValue = e.target.value;
    setFilter(searchValue);

    // Filter the tableData based on the input
    const filtered = tableData.filter((table) =>
      table.table_code.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredTableData(filtered); // Update the filtered table data
  };

  // Handle page change for pagination
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Get current page data
  const paginateData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // Open the modal for adding or editing a table
  const handleOpenModal = (table = null) => {
    setSelectedTable(table);
    setOpen(true);
  };

  // Close the modal
  const handleCloseModal = () => {
    setSelectedTable(null);
    setOpen(false);
  };

  // Open the delete confirmation dialog
  const handleOpenDeleteConfirmation = (id) => {
    setTableToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  // Close the delete confirmation dialog
  const handleCloseDeleteConfirmation = () => {
    setTableToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  // Handle soft delete
  const handleDeleteTable = async () => {
    if (!tableToDelete) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/Tables/soft_delete/${tableToDelete}`
      );
      if (response.data.status_code === 200) {
        fetchData(); // Reload data after successful deletion
        setError("");
        toast("Table deleted successfully!"); // Success message
      } else {
        setError(response.data.status_message || "Failed to delete table.");
        toast("Failed to delete table."); // Error message
      }
    } catch (err) {
      setError("An error occurred while deleting the table.");
      toast("An error occurred while deleting the table."); // Error message
    } finally {
      setLoading(false);
      handleCloseDeleteConfirmation();
    }
  };

  // Success Handler for add/update
  const handleSuccess = (action) => {
    if (action === "add") {
      toast("Table added successfully!"); // Success message for adding
    } else if (action === "update") {
      toast("Table updated successfully!"); // Success message for updating
    }
  };

  // Error Handler for add/update
  const handleError = (message) => {
    toast(message); // Display the error message
  };

  useEffect(() => {
    fetchData(); // Fetch data on initial render
  }, []); // Run only once when the component mounts

  useEffect(() => {
    if (error) {
      toast(error); // Show error using toast
    }
  }, [error]);

  return (
    <>
      <SideBar />
      <Header />
      <div className="MainContainer">
        <div className="MenuHeader">
          <div className="CategoryFilter">
            <TextField
              label="Filter by name"
              size="small"
              variant="outlined"
              fullWidth
              value={filter} // Bind filter value to state
              onChange={handleFilterChange} // Handle filter input change
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal()}
            size="small"
          >
            Add New Table
          </Button>
        </div>
        <TableLists
          tableData={paginateData(filteredTableData)} // Paginate the filtered data
          handleOpenModal={handleOpenModal}
          handleDeleteTable={handleOpenDeleteConfirmation} // Open delete confirmation dialog
        />
        {/* Pagination */}
        <Pagination
          count={Math.ceil(filteredTableData.length / itemsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size="small"
        />
        {open && (
          <TableForm
            open={open}
            handleClose={handleCloseModal}
            fetchData={fetchData}
            selectedTable={selectedTable}
            handleSuccess={handleSuccess} // Pass success handler for add/update
            handleError={handleError} // Pass error handler for add/update
          />
        )}
        {/* Confirmation Dialog */}
        <Dialog
          open={deleteConfirmationOpen}
          onClose={handleCloseDeleteConfirmation}
          aria-labelledby="delete-confirmation-dialog"
        >
          <DialogTitle id="delete-confirmation-dialog">
            Are you sure you want to delete this table?
          </DialogTitle>
          <DialogContent>
            <p>This action cannot be undone.</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteConfirmation} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTable}
              color="secondary"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <Footer />
    </>
  );
}

export default CreateTable;
